import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { AuthHead, Notice } from "@/components/AuthFrame";
import { RecoveryCodes } from "@/features/auth/RecoveryCodes";
import { authErrorText } from "@/features/auth/errors";
import { meQuery, renewRecoveryCodes } from "@/lib/auth";

// Nieuwe herstelcodes (H-g02): with the password, eight new ones, shown once; the ones of before stop.

export const Route = createFileRoute("/_portal/account/recovery-codes")({
  component: RecoveryCodesScreen,
});

function RecoveryCodesScreen() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: me } = useSuspenseQuery(meQuery);
  const [password, setPassword] = useState("");
  const [codes, setCodes] = useState<string[] | null>(null);
  const [kept, setKept] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const answer = await renewRecoveryCodes(password);
      setCodes(answer.recoveryCodes);
      queryClient.setQueryData(meQuery.queryKey, { ...me, recoveryCodesLeft: answer.recoveryCodesLeft });
    } catch (err) {
      setError(authErrorText(t, err, i18n.language));
    } finally {
      setPassword("");
      setBusy(false);
    }
  };

  return (
    <div className="wl-auth" data-screen-label="Portaal Nieuwe herstelcodes">
      <div className="wl-auth__card">
        {codes ? (
          <>
            <AuthHead title={t("codes.title")} lead={t("codes.leadNew")} />
            <RecoveryCodes codes={codes} email={me.user.email} kept={kept} onKept={setKept} />
            <div className="wl-auth__actions">
              <span className="wl-spacer" />
              <button type="button" className="wl-btn wl-btn--primary wl-btn--lg" disabled={!kept} onClick={() => void navigate({ to: "/shipments" })}>
                {t("account.backToPortal")}
              </button>
            </div>
          </>
        ) : (
          <form
            className="wl-vstack"
            style={{ gap: "var(--space-5)", alignItems: "stretch" }}
            onSubmit={(event) => {
              event.preventDefault();
              void submit();
            }}
          >
            <AuthHead title={t("user.recoveryCodes")} lead={t("account.codesLead", { count: me.recoveryCodesLeft })} />
            {error && <Notice tone="danger">{error}</Notice>}
            <div className="wl-field">
              <label className="wl-field__label" htmlFor="codes-password">
                {t("password.current")}
              </label>
              <input
                id="codes-password"
                className="wl-input"
                type="password"
                autoComplete="current-password"
                value={password}
                autoFocus
                onChange={(event) => setPassword(event.target.value)}
              />
            </div>
            <div className="wl-auth__actions">
              <button type="button" className="wl-btn wl-btn--ghost" onClick={() => void navigate({ to: "/shipments" })}>
                {t("common.cancel")}
              </button>
              <span className="wl-spacer" />
              <button type="submit" className="wl-btn wl-btn--primary wl-btn--lg" disabled={busy || password === ""}>
                {t("account.codesSubmit")}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
